import * as React from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { Popover } from '../../ui/Popover'
import type { GridSelectOption } from './types'

interface SelectCellProps {
  value: string
  options: readonly GridSelectOption[]
  onChange: (value: string) => void
  label: string
  invalid?: boolean
}

/** Chip + chevron that opens a searchable list, matching the design's rarity cell. */
export function SelectCell({ value, options, onChange, label, invalid }: SelectCellProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  // A stale query would make the next open show a filtered list for no reason.
  const [wasOpen, setWasOpen] = React.useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setQuery('')
  }

  const current = options.find((option) => option.value === value)
  const q = query.trim().toLowerCase()
  const matches = options.filter((option) => option.label.toLowerCase().includes(q))

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      className="w-50 p-1"
      trigger={
        <button
          type="button"
          aria-label={label}
          className={cn(
            'flex w-full items-center justify-between gap-1 rounded-sm border border-transparent px-1 py-0.5 text-left transition-colors duration-100 ease-out outline-none',
            invalid && 'border-err bg-err-surface',
            'hover:bg-bg-hover focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-1',
          )}
        >
          <span
            className={cn(
              'rounded-sm px-2 py-0.5 text-sm',
              current?.chipClassName ?? 'bg-bg-subtle text-fg-muted',
            )}
          >
            {current?.label ?? '—'}
          </span>
          <ChevronDown className="text-fg-subtle size-3 shrink-0" />
        </button>
      }
    >
      <div className="border-border text-fg-subtle mb-1 flex items-center gap-1.5 border-b px-1.5 pb-1.5">
        <Search className="size-3 shrink-0" />
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${label.toLowerCase()}…`}
          aria-label={`Search ${label}`}
          className="text-fg placeholder:text-fg-subtle min-w-0 flex-1 border-0 bg-transparent text-base outline-none"
        />
      </div>

      {matches.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => {
            onChange(option.value)
            setOpen(false)
          }}
          className="hover:bg-bg-hover flex w-full items-center justify-between gap-2 rounded-sm px-1.5 py-1 transition-colors duration-100 ease-out"
        >
          <span
            className={cn(
              'rounded-sm px-2 py-0.5 text-sm',
              option.chipClassName ?? 'bg-bg-subtle text-fg-muted',
            )}
          >
            {option.label}
          </span>
          {option.value === value && <Check className="text-fg-muted size-3 shrink-0" />}
        </button>
      ))}

      {matches.length === 0 && (
        <p className="text-fg-subtle m-0 px-1.5 py-2.5 text-center text-sm">No match.</p>
      )}
    </Popover>
  )
}
